// Page-view tracking. Visitor geo/IP is resolved at the edge by the Cloudflare
// Worker (a browser can't determine its own IP or location) and forwarded here
// with the snapshot; this module posts it to the backend.
//
// Nothing is sent until the visitor accepts the cookie banner — see
// PrivacyPolicy.tsx and CookieBanner.tsx.
import { api, type PageViewPayload, type VisitorInfo } from './api';

const CONSENT_KEY = 'analytics_consent';
const SESSION_KEY = 'analytics_session';

export type Consent = 'granted' | 'denied' | null;

export function getConsent(): Consent {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    return v === 'granted' || v === 'denied' ? v : null;
  } catch {
    return null; // private mode / storage blocked
  }
}

export function setConsent(value: Exclude<Consent, null>) {
  try {
    localStorage.setItem(CONSENT_KEY, value);
  } catch { /* nothing we can do */ }
  if (value === 'denied') clearAnalyticsStorage();
}

function clearAnalyticsStorage() {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch { /* ignore */ }
}

/** Per-tab id so repeat views can be grouped into a visit. */
export function sessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return 'no-storage';
  }
}

// Geo handed over by the Worker; set once the snapshot loads.
let visitor: VisitorInfo | null = null;
export function setVisitorInfo(info: VisitorInfo | undefined | null) {
  if (info) visitor = info;
}

let lastPath: string | null = null;
let enteredAt = Date.now();

// Views attempted before consent, so accepting the banner doesn't lose the
// visit that was already in progress.
let pending: string | null = null;

/**
 * Record a page view. No-op unless consent was granted.
 * Failures are swallowed — analytics must never break the page.
 *
 * `duration_ms` measures time on the *previous* view, so the first call of a
 * visit sends 0 and each later one reports how long the last section was read.
 */
export function trackPageView(path: string) {
  if (path === lastPath) return; // guard double-fire from router effects

  if (getConsent() !== 'granted') {
    pending = path; // replay this once/if they accept
    return;
  }

  const now = Date.now();
  const payload: PageViewPayload = {
    path,
    referrer: document.referrer || undefined,
    session_id: sessionId(),
    duration_ms: lastPath === null ? 0 : now - enteredAt,
    ...(visitor ?? {}),
  };

  lastPath = path;
  enteredAt = now;
  pending = null;

  api.analytics.collect(payload).catch(() => { /* never surface to the user */ });
}

/** Called by the banner on Accept — records the view already in progress. */
export function flushPending() {
  const path = pending;
  if (!path || getConsent() !== 'granted') return;
  pending = null;
  lastPath = null; // the pending path was never actually sent
  trackPageView(path);
}
